import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { VideoDescriptor } from '@libs/waymark-video/video-descriptor';
import WaymarkAuthorWebRenderer from 'shared/WaymarkAuthorWebRenderer';

export const waymarkVideoRendererContext = createContext<{
  renderer: WaymarkAuthorWebRenderer;
  isRendererSetup: boolean;
}>({
  renderer: {} as WaymarkAuthorWebRenderer,
  isRendererSetup: false,
});

export interface WaymarkVideoRendererProviderProps {
  videoDescriptor: VideoDescriptor;
  children: React.ReactNode;
}

export default function WaymarkVideoRendererProvider({
  videoDescriptor,
  children,
}: WaymarkVideoRendererProviderProps) {
  const [renderer, setRenderer] = useState<WaymarkAuthorWebRenderer>(
    () => new WaymarkAuthorWebRenderer({ videoDescriptor }),
  );
  const [isSetup, setIsSetup] = useState(false);

  useEffect(() => {
    if (renderer.videoDescriptor !== videoDescriptor) {
      // If the VideoDescriptor instance changed, create a new renderer instance
      // to replace the old one
      setIsSetup(false);
      const newRenderer = new WaymarkAuthorWebRenderer({
        videoDescriptor,
      });
      setRenderer(newRenderer);
    }
  }, [renderer, videoDescriptor]);

  useEffect(() => {
    const onSetup = () => {
      setIsSetup(true);
    };
    renderer.on('setup:end', onSetup);
    renderer.setup({});

    // Tear down renderer on unmount or if the renderer changes
    return () => {
      renderer.off('setup:end', onSetup);
      renderer.tearDown();
    };
  }, [renderer]);

  const contextValue = useMemo(() => ({ renderer, isRendererSetup: isSetup }), [renderer, isSetup]);

  return (
    <waymarkVideoRendererContext.Provider value={contextValue}>
      {children}
    </waymarkVideoRendererContext.Provider>
  );
}

export const useWaymarkVideoRenderer = () => useContext(waymarkVideoRendererContext);

export const useRendererFramerate = () => {
  const { renderer } = useWaymarkVideoRenderer();
  return renderer.framerate;
};

export const useJumpVideoToTime = (shouldPause = true) => {
  const { renderer, isRendererSetup } = useWaymarkVideoRenderer();
  const pausePlayback = usePauseVideoPlayback();

  return useCallback(
    async (newTime: number) => {
      if (isRendererSetup) {
        await renderer.goToFrame(Math.round(newTime * renderer.framerate));

        if (renderer.isPlaying && shouldPause) {
          pausePlayback();
        }
      }
    },
    [isRendererSetup, pausePlayback, renderer, shouldPause],
  );
};

export const useStartVideoPlayback = () => {
  const { renderer, isRendererSetup } = useWaymarkVideoRenderer();
  return useCallback(async () => {
    if (isRendererSetup && !renderer.isPlaying) {
      await renderer.play();
    }
  }, [isRendererSetup, renderer]);
};

export const usePauseVideoPlayback = () => {
  const { renderer, isRendererSetup } = useWaymarkVideoRenderer();
  return useCallback(async () => {
    if (isRendererSetup && renderer.isPlaying) {
      await renderer.stop();
    }
  }, [isRendererSetup, renderer]);
};
